import "regenerator-runtime/runtime"; // fontkit's Indic shaper needs this global
import { degrees, rgb, StandardFonts, type PDFDocument, type PDFFont, type PDFPage } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { browserRasterEnv, fetchDevanagariFont, type FallbackLoader, canWinAnsi, RASTER_ASCENT_EM, RASTER_HEIGHT_EM, SYMBOL_FALLBACKS, sanitizeForWinAnsiSingleLine, type RasterEnv } from "./pdfFonts";

/**
 * One line of text on an existing page, in any script. Text the built-in fonts can draw is real text;
 * anything else (Chinese, Arabic, Hindi, emoji ...) is drawn by the browser and placed as a picture, so
 * a watermark, a typed name or a note in those languages is no longer turned into "?".
 */
export const needsPicture = (text: string): boolean => {
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    if (cp < 0x20 || cp === 0x7f || canWinAnsi(cp) || SYMBOL_FALLBACKS[ch] !== undefined) continue;
    if ((cp >= 0x200b && cp <= 0x200f) || cp === 0xfeff) continue;
    return true;
  }
  return false;
};

const DEVA_RE = /[\u0900-\u097f]/;
const devaCache = new WeakMap<PDFDocument, Promise<{ font: PDFFont; chars: Set<number> } | null>>();
const defaultDeva = (): FallbackLoader | undefined => (typeof document !== "undefined" ? fetchDevanagariFont : undefined);

/** The Devanagari font embedded once per document (null when it cannot be loaded). */
function devanagariFor(pdf: PDFDocument, loader: FallbackLoader): Promise<{ font: PDFFont; chars: Set<number> } | null> {
  let p = devaCache.get(pdf);
  if (!p) {
    p = (async () => {
      try {
        pdf.registerFontkit(fontkit);
        const font = await pdf.embedFont(await loader(), { subset: false }); // fontkit drops glyphs when subsetting this font
        return { font, chars: new Set(font.getCharacterSet()) };
      } catch { return null; }
    })();
    devaCache.set(pdf, p);
  }
  return p;
}

/** Hindi / Marathi / Nepali text (with Latin and digits) is drawn as real, selectable text when the font has every character. */
async function realDevanagari(pdf: PDFDocument, text: string, loader: FallbackLoader | null | undefined): Promise<PDFFont | null> {
  const l = loader === undefined ? defaultDeva() : loader;
  if (!l || !DEVA_RE.test(text)) return null;
  const d = await devanagariFor(pdf, l);
  if (!d) return null;
  for (const ch of text) if (!d.chars.has(ch.codePointAt(0)!)) return null;
  return d.font;
}

export interface DrawAnywhereOptions {
  /** Devanagari font loader (default: this site's file in a browser); null turns real Devanagari text off. */
  devaLoader?: FallbackLoader | null;
  x: number;
  y: number;
  size: number;
  color?: { r: number; g: number; b: number };
  bold?: boolean;
  opacity?: number;
  rotateDegrees?: number;
  /** Test double; defaults to the browser canvas. */
  env?: RasterEnv | null;
}

export async function measureTextAnywhere(pdf: PDFDocument, text: string, size: number, bold = false, env: RasterEnv | null = browserRasterEnv(), devaLoader?: FallbackLoader | null): Promise<number> {
  const deva = await realDevanagari(pdf, text, devaLoader);
  if (deva) return deva.widthOfTextAtSize(text, size);
  if (env && needsPicture(text)) return env.measure(text, "sans", bold, false) * size;
  const font = await pdf.embedFont(bold ? StandardFonts.HelveticaBold : StandardFonts.Helvetica);
  return font.widthOfTextAtSize(sanitizeForWinAnsiSingleLine(text), size);
}

export async function drawTextAnywhere(pdf: PDFDocument, page: PDFPage, text: string, o: DrawAnywhereOptions): Promise<void> {
  const color = o.color ?? { r: 0, g: 0, b: 0 };
  const env = o.env === undefined ? browserRasterEnv() : o.env;
  const rotate = o.rotateDegrees ?? 0;
  const deva = await realDevanagari(pdf, text, o.devaLoader);
  if (deva) {
    page.drawText(text, { x: o.x, y: o.y, size: o.size, font: deva, color: rgb(color.r, color.g, color.b), opacity: o.opacity, rotate: degrees(rotate) });
    return;
  }
  if (env && needsPicture(text)) {
    const { png, widthEm } = await env.render(text, "sans", !!o.bold, false, [color.r, color.g, color.b]);
    const img = await pdf.embedPng(png);
    const a = (rotate * Math.PI) / 180;
    const down = (RASTER_HEIGHT_EM - RASTER_ASCENT_EM) * o.size;
    const back = 0.1 * o.size;
    page.drawImage(img, {
      x: o.x + down * Math.sin(a) - back * Math.cos(a),
      y: o.y - down * Math.cos(a) - back * Math.sin(a),
      width: widthEm * o.size,
      height: RASTER_HEIGHT_EM * o.size,
      rotate: degrees(rotate),
      opacity: o.opacity,
    });
    return;
  }
  const font = await pdf.embedFont(o.bold ? StandardFonts.HelveticaBold : StandardFonts.Helvetica);
  page.drawText(sanitizeForWinAnsiSingleLine(text), { x: o.x, y: o.y, size: o.size, font, color: rgb(color.r, color.g, color.b), opacity: o.opacity, rotate: degrees(rotate) });
}
